import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Lock,
  AlertCircle,
  Tag,
  HelpCircle,
  Check,
  ChevronsUpDown,
  Search,
  X,
  Network,
  LayoutGrid,
  List,
  Boxes,
  Building2,
  ShieldCheck,
  Cog,
  Clock,
  DollarSign,
  Leaf,
  Puzzle,
  Fingerprint,
  Crosshair,
  ListChecks,
  Settings2,
  Save,
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import {
  entityTypeService,
  type EntityType,
  type EntityTypeCategory,
  type EntityTypeCreate,
  type EntityTypeUpdate,
  getCategoryInfo,
} from '@/services/kbService';
import DeleteConfirmDialog from '@/components/knowledge/DeleteConfirmDialog';
import SortableTableHeader, { useSortableTable } from '@/components/knowledge/SortableTableHeader';
import DataTablePagination from '@/components/knowledge/DataTablePagination';
import RefreshButton from '@/components/knowledge/RefreshButton';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

import { logger } from '@/utils/logger';
type SortColumn = 'name' | 'importance_weight';

/** Centered section divider used inside the entity configuration dialog */
const ConfigSectionDivider: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-3 pt-1">
    <div className="h-px flex-1 bg-border" />
    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80">
      {icon}
      {label}
    </span>
    <div className="h-px flex-1 bg-border" />
  </div>
);

/** Code-editor style shell (chrome bar + line-number gutter) for pattern/vocab textareas */
const CodeEditorShell: React.FC<{
  filename: string;
  value: string;
  minLines?: number;
  children: React.ReactNode;
}> = ({ filename, value, minLines = 3, children }) => {
  const lineCount = Math.max(minLines, value.split('\n').length);
  return (
    <div className="rounded-lg border border-border bg-black/30 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-1.5" aria-hidden="true">
          <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
          <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
          <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">{filename}</span>
      </div>
      <div className="flex">
        <div className="select-none px-2.5 py-2 text-right font-mono text-xs leading-6 text-muted-foreground/50 bg-muted/10 border-r border-border/60" aria-hidden="true">
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        {children}
      </div>
    </div>
  );
};

/** SVG progress ring for importance weight / confidence */
const WeightRing: React.FC<{ value: number; className?: string }> = ({ value, className }) => {
  const r = 14;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, value));
  return (
    <svg viewBox="0 0 36 36" className={`-rotate-90 ${className || 'h-9 w-9'}`} aria-hidden="true">
      <circle cx="18" cy="18" r={r} fill="none" strokeWidth="3" className="stroke-muted" />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${c * clamped} ${c}`}
        className="stroke-current"
      />
    </svg>
  );
};

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  core: Network,
  operational: Cog,
  compliance: ShieldCheck,
  temporal: Clock,
  financial: DollarSign,
  natural: Leaf,
  business: Building2,
  custom: Puzzle,
};

const KBEntityTypesPage: React.FC = () => {
  // Data state
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  // Full catalogue (names/labels only used here) for the Parent Type
  // dropdown, kept independent of the paginated table so a parent can be
  // chosen from ANY type, not just the current page's 25 rows.
  const [allTypes, setAllTypes] = useState<EntityType[]>([]);
  const [categories, setCategories] = useState<EntityTypeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reloadingRegistry, setReloadingRegistry] = useState(false);

  // Statistics
  const [statistics, setStatistics] = useState<{
    database: Record<string, number>;
    registry: Record<string, number>;
    in_sync: boolean;
  } | null>(null);

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  // Status filter: 'all' (default) shows active + inactive so the row count
  // matches the "Total Types" summary card; 'active' / 'inactive' narrow it.
  // (Was a binary Active-Only/Show-Inactive switch that could only ever show
  // ONE status, so the list total never matched the all-statuses card.)
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  // Governance toggles: show system types (on) and show the full,
  // unscoped catalogue (on by default) so in-use types that aren't
  // whitelisted by the active Domain Knowledge Template still appear.
  const [includeSystem, setIncludeSystem] = useState(true);
  const [fullCatalogue, setFullCatalogue] = useState(true);
  const [vocabularyFilter, setVocabularyFilter] = useState<'all' | 'with' | 'without'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Debounced copy of searchQuery - the actual value sent to the backend,
  // so typing doesn't fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Server-side pagination state. The list, total, sort and all filters
  // are resolved by the backend (get_entity_types_page) so `total` is
  // accurate for the active filter set and each page is a correct slice.
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 25;

  // Grid / list presentation toggle (from the Navy & Sky redesign). Purely a
  // rendering choice - the rows themselves still come from the server page.
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Sorting state (using shared hook)
  const { sortColumn, sortDirection, handleSort } = useSortableTable<SortColumn>();

  // Modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingEntityType, setEditingEntityType] = useState<EntityType | null>(null);
  const [saving, setSaving] = useState(false);
  // Parent Type searchable picker state. Rendered INLINE (not via a Radix
  // Popover portal) so the mouse wheel works: the edit modal is a Radix
  // Dialog wrapped in react-remove-scroll, which blocks wheel events on any
  // portalled content rendered outside the dialog subtree.
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [parentSearch, setParentSearch] = useState('');
  const parentPickerRef = useRef<HTMLDivElement>(null);

  // Close the inline picker on an outside click.
  useEffect(() => {
    if (!parentPickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (parentPickerRef.current && !parentPickerRef.current.contains(e.target as Node)) {
        setParentPickerOpen(false);
        setParentSearch('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [parentPickerOpen]);

  // Delete confirmation state
  const [deleteEntityId, setDeleteEntityId] = useState<number | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    label: string;
    description: string;
    category: string;
    parent_type: string;
    importance_weight: number;
    is_active: boolean;
    is_universal: boolean;
    collapse_to_parent: boolean;
    vocabulary: string;
    patterns: string;
  }>({
    name: '',
    label: '',
    description: '',
    category: 'core',
    parent_type: '',
    importance_weight: 0.5,
    is_active: true,
    is_universal: false,
    collapse_to_parent: false,
    vocabulary: '',
    patterns: '',
  });

  // Debounce the search box (300ms) and reset to page 1 on a new term.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Any filter/sort change resets to the first page. Kept separate from
  // the data-loading effect so changing a filter doesn't fetch the old
  // page number first.
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, statusFilter, includeSystem, fullCatalogue, vocabularyFilter, sortColumn, sortDirection]);

  // Initial load: table + categories + statistics together (one shot, with
  // the page spinner).
  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TABLE ONLY: reload the page slice when a query input changes. Search is
  // debounced (via debouncedSearch), so typing a letter hits ONLY
  // get_entity_types_page - and only ~300ms after the last keystroke. The
  // category-count and statistics endpoints are deliberately NOT in here.
  const skipFirstTableLoad = useRef(true);
  useEffect(() => {
    if (skipFirstTableLoad.current) {
      skipFirstTableLoad.current = false;
      return;
    }
    loadEntityTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, categoryFilter, statusFilter,
      includeSystem, fullCatalogue, vocabularyFilter, sortColumn, sortDirection]);

  // STATISTICS: the summary cards reflect the active filter set, so reload
  // them whenever any filter changes (search is debounced) - but NOT on
  // paging or sorting, which don't change the totals.
  const skipFirstStats = useRef(true);
  useEffect(() => {
    if (skipFirstStats.current) {
      skipFirstStats.current = false;
      return;
    }
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullCatalogue, debouncedSearch, categoryFilter, statusFilter, includeSystem, vocabularyFilter]);

  const loadData = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      await Promise.all([
        loadEntityTypes(),
        loadCategories(),
        loadStatistics(),
      ]);
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  const loadEntityTypes = async () => {
    try {
      const orderBy = sortColumn
        ? `${sortColumn} ${sortDirection}`
        : undefined;

      const result = await entityTypeService.getEntityTypesPage({
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        search: debouncedSearch || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        // 'all' | 'active' | 'inactive' - backend filters on is_active.
        status: statusFilter,
        include_system: includeSystem,
        // false => full, unscoped catalogue (governance default);
        // true => narrow to the active template's whitelist.
        scope_to_active_template: !fullCatalogue,
        order_by: orderBy,
        has_vocabulary: vocabularyFilter,
      });

      // If a delete emptied the current (non-first) page, step back one.
      if (result.data.length === 0 && result.total > 0 && currentPage > 1) {
        setCurrentPage(p => Math.max(1, p - 1));
        return;
      }

      setEntityTypes(result.data);
      setTotalItems(result.total);
    } catch (error: any) {
      logger.error('Failed to load entity types:', error);
      toast.error('Failed to load entity types');
    }
  };

  // Full catalogue for the Parent Type picker. Loaded lazily when the
  // create/edit modal opens (not on page load) so the table's pagination
  // isn't undermined by a 1000-row fetch nobody asked for. Not tied to
  // the table's pagination/filters.
  const loadAllTypes = async () => {
    try {
      const result = await entityTypeService.getEntityTypesPage({
        limit: 1000,
        status: 'all',
        include_system: true,
        scope_to_active_template: false,
      });
      setAllTypes(result.data);
    } catch (error: any) {
      logger.error('Failed to load full type catalogue:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await entityTypeService.getCategories();
      setCategories(data);
    } catch (error: any) {
      logger.error('Failed to load categories:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      // Keep the summary cards in step with the row list: same scope AND
      // same filters, so the metrics reflect the active filter set.
      const data = await entityTypeService.getStatistics(!fullCatalogue, {
        search: debouncedSearch || undefined,
        category: categoryFilter,
        status: statusFilter,
        include_system: includeSystem,
        has_vocabulary: vocabularyFilter,
      });
      setStatistics(data);
    } catch (error: any) {
      logger.error('Failed to load statistics:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // True when any filter is off its default - drives the Clear button.
  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    categoryFilter !== 'all' ||
    statusFilter !== 'all' ||
    vocabularyFilter !== 'all' ||
    !includeSystem ||
    !fullCatalogue;

  // Reset every filter control back to its default. Search debounce +
  // the filter effects then reload the table and the summary cards.
  const handleClearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
    setStatusFilter('all');
    setVocabularyFilter('all');
    setIncludeSystem(true);
    setFullCatalogue(true);
  };

  const handleReloadRegistry = async () => {
    setReloadingRegistry(true);
    try {
      const result = await entityTypeService.reloadRegistry();
      if (result.success) {
        toast.success(result.message || 'Registry reloaded successfully');
        await loadData();
      } else {
        toast.warning('Registry reload completed with warnings');
      }
    } catch (error: any) {
      logger.error('Failed to reload registry:', error);
      toast.error(error.response?.data?.detail || 'Failed to reload registry');
    } finally {
      setReloadingRegistry(false);
    }
  };

  const handleOpenCreateModal = () => {
    loadAllTypes(); // populate the Parent Type picker
    setEditingEntityType(null);
    setFormData({
      name: '',
      label: '',
      description: '',
      category: 'custom',
      parent_type: '',
      importance_weight: 0.8,
      is_active: true,
      is_universal: false,
      collapse_to_parent: false,
      vocabulary: '',
      patterns: '',
    });
    setEditModalVisible(true);
  };

  const handleOpenEditModal = (entityType: EntityType) => {
    loadAllTypes(); // populate the Parent Type picker
    setEditingEntityType(entityType);
    setFormData({
      name: entityType.name,
      label: entityType.label,
      description: entityType.description || '',
      category: entityType.category,
      parent_type: entityType.parent_type || '',
      importance_weight: entityType.importance_weight,
      is_active: entityType.is_active,
      // Backend may omit is_universal on older catalogue rows that
      // pre-date the column; default to false in that case so the
      // toggle starts in the safe state.
      is_universal: Boolean(entityType.is_universal),
      collapse_to_parent: Boolean(entityType.collapse_to_parent),
      vocabulary: entityType.vocabulary?.join(', ') || '',
      patterns: entityType.patterns?.join('\n') || '',
    });
    setEditModalVisible(true);
  };

  const handleCloseModal = () => {
    setEditModalVisible(false);
    setEditingEntityType(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.label.trim()) {
      toast.error('Name and Label are required');
      return;
    }

    setSaving(true);
    try {
      const vocabularyArray = formData.vocabulary
        .split(',')
        .map(v => v.trim())
        .filter(v => v);

      const patternsArray = formData.patterns
        .split('\n')
        .map(p => p.trim())
        .filter(p => p);

      // Guard: synonym-collapse still needs a master to migrate into.
      if (formData.collapse_to_parent && !formData.parent_type) {
        toast.error('Set a Parent Type before enabling "Collapse into parent".');
        setSaving(false);
        return;
      }

      if (editingEntityType) {
        // Update existing - always send vocabulary/patterns (even empty) so clearing works
        const updateData: EntityTypeUpdate = {
          label: formData.label,
          description: formData.description || undefined,
          category: formData.category,
          parent_type: formData.parent_type,
          importance_weight: formData.importance_weight,
          is_active: formData.is_active,
          is_universal: formData.is_universal,
          collapse_to_parent: formData.collapse_to_parent,
          vocabulary: vocabularyArray,
          patterns: patternsArray,
        };

        await entityTypeService.updateEntityType(editingEntityType.id, updateData);
        toast.success(
          formData.collapse_to_parent && !editingEntityType.collapse_to_parent
            ? `Collapsing ${formData.name} into ${formData.parent_type}…`
            : 'Entity type updated successfully'
        );
      } else {
        // Create new
        const createData: EntityTypeCreate = {
          name: formData.name.toUpperCase().replace(/\s+/g, '_'),
          label: formData.label,
          description: formData.description || undefined,
          category: formData.category,
          parent_type: formData.parent_type,
          importance_weight: formData.importance_weight,
          is_active: formData.is_active,
          is_universal: formData.is_universal,
          vocabulary: vocabularyArray.length > 0 ? vocabularyArray : undefined,
          patterns: patternsArray.length > 0 ? patternsArray : undefined,
        };

        await entityTypeService.createEntityType(createData);
        toast.success('Entity type created successfully');
      }

      handleCloseModal();
      // Deliberate action (not a keystroke): refresh table, category counts
      // and stat cards together.
      loadData();
    } catch (error: any) {
      logger.error('Failed to save entity type:', error);
      // Frappe returns errors in _server_messages or exc_type
      let msg = 'Failed to save entity type';
      if (error.response?.data?._server_messages) {
        try {
          const msgs = JSON.parse(error.response.data._server_messages);
          const parsed = JSON.parse(msgs[0]);
          msg = parsed.message || msg;
        } catch { /* use default */ }
      } else if (error.response?.data?.exception) {
        msg = error.response.data.exception.split('\n').pop() || msg;
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = (id: number) => {
    setDeleteEntityId(id);
    setDeleteConfirmVisible(true);
  };

  const handleDelete = async () => {
    if (!deleteEntityId) return;

    setDeleting(true);
    try {
      await entityTypeService.deleteEntityType(deleteEntityId);
      toast.success('Entity type deleted successfully');
      setDeleteConfirmVisible(false);
      setDeleteEntityId(null);
      // If deletion was triggered from inside the configuration dialog, close it too
      setEditModalVisible(false);
      setEditingEntityType(null);
      loadData();
    } catch (error: any) {
      logger.error('Failed to delete entity type:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entity type');
    } finally {
      setDeleting(false);
    }
  };

  const getImportanceColor = (weight: number): string => {
    if (weight >= 0.8) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    if (weight >= 0.5) return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
    return 'bg-muted text-muted-foreground border-border';
  };

  const getCategoryColor = (category: string): string => {
    const info = getCategoryInfo(category);
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
      red: 'bg-red-500/10 text-red-400 border-red-500/30',
      purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      yellow: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
      lime: 'bg-lime-500/10 text-lime-400 border-lime-500/30',
      cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
      pink: 'bg-pink-500/10 text-pink-400 border-pink-500/30',
      orange: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    };
    return colorMap[info.color] || 'bg-muted text-muted-foreground border-border';
  };

  // Use statistics from API (matches reference behavior)
  const totalTypes = statistics?.database?.total_types ?? entityTypes.length;
  const systemTypes = statistics?.database?.system_types ?? entityTypes.filter(e => e.is_system).length;
  const customTypes = statistics?.database?.custom_types ?? (totalTypes - systemTypes);
  const typesWithVocab = statistics?.database?.types_with_vocabulary ?? entityTypes.filter(e => e.vocabulary && e.vocabulary.length > 0).length;

  // The backend already filters, sorts and paginates - render the page
  // slice as-is.
  const sortedEntityTypes = entityTypes;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Average importance across the rows currently on screen. The backend exposes
  // no catalogue-wide average, and `entityTypes` is only the current 25-row
  // page - so this is labelled as a page figure rather than passed off as a
  // global stat.
  const avgConfidence = sortedEntityTypes.length > 0
    ? sortedEntityTypes.reduce((sum, e) => sum + e.importance_weight, 0) / sortedEntityTypes.length
    : 0;

  // Parent Type picker options: curated catalogue only (exclude
  // auto-discovered / LLM-invented noise) and never the type being edited.
  const parentCandidates = allTypes
    .filter(e => !e.auto_discovered)
    .filter(e => !editingEntityType || e.name !== editingEntityType.name)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const parentSearchQ = parentSearch.trim().toLowerCase();
  const filteredParentCandidates = parentSearchQ
    ? parentCandidates.filter(e =>
        e.name.toLowerCase().includes(parentSearchQ) ||
        (e.label || '').toLowerCase().includes(parentSearchQ))
    : parentCandidates;
  const selectedParent = formData.parent_type
    ? allTypes.find(t => t.name === formData.parent_type)
    : undefined;

  return (
    <div className="dark bg-background text-foreground min-h-full max-w-full p-6">
      <div className="grid gap-6 lg:grid-cols-[230px_minmax(0,1fr)]">
        {/* Domains rail. Replaces the old "Filter by category" Select - it drives
            the same server-side categoryFilter, so paging and totals stay correct. */}
        <aside className="space-y-4">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Administration</p>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Entity Types</h1>
          </div>
          <div className="rounded-xl border border-border bg-card/40 p-2">
            <p className="px-2 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Domains</p>
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setCategoryFilter('all')}
                className={`w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors border ${categoryFilter === 'all' ? 'bg-primary/10 text-foreground border-primary/40' : 'text-muted-foreground hover:bg-muted/50 border-transparent'}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Boxes className="h-4 w-4 shrink-0" />
                  <span className="truncate">All Categories</span>
                </span>
                <span className="font-mono text-xs">{totalTypes}</span>
              </button>
              {categories.map(cat => {
                const CategoryIcon = CATEGORY_ICONS[cat.category] || Tag;
                return (
                  <button
                    key={cat.category}
                    type="button"
                    onClick={() => setCategoryFilter(cat.category)}
                    className={`w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors border ${categoryFilter === cat.category ? 'bg-primary/10 text-foreground border-primary/40' : 'text-muted-foreground hover:bg-muted/50 border-transparent'}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <CategoryIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{cat.label}</span>
                    </span>
                    <span className="font-mono text-xs">{cat.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Main column */}
        <div className="space-y-6 min-w-0">
          {/* Toolbar. Every control here is a SERVER-side filter (see loadData) -
              the list, its total and its sort are all resolved by the backend, so
              filtering must never be re-implemented client-side over the page slice. */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, label or parent type.."
                className="pl-10"
              />
            </div>

            {/* Status filter: 'all' shows active + inactive so the row count matches
                the Total Entities card. (Not a binary Active/Inactive switch - that
                could only ever show ONE status.) */}
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive')}
            >
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={vocabularyFilter}
              onValueChange={(v) => setVocabularyFilter(v as 'all' | 'with' | 'without')}
            >
              <SelectTrigger className="w-full sm:w-[170px]">
                <SelectValue placeholder="Vocabulary" />
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value="all">All Vocabulary</SelectItem>
                <SelectItem value="with">With Vocabulary</SelectItem>
                <SelectItem value="without">Without Vocabulary</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Switch
                id="show-system"
                checked={includeSystem}
                onCheckedChange={setIncludeSystem}
              />
              <Label htmlFor="show-system" className="text-sm whitespace-nowrap">
                Include System Types
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="full-catalogue"
                checked={fullCatalogue}
                onCheckedChange={setFullCatalogue}
              />
              <Label htmlFor="full-catalogue" className="flex items-center gap-1 text-sm whitespace-nowrap">
                Full Catalogue
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="dark">
                      On: show every entity type in the catalogue. Off: show only
                      the types whitelisted by the active Domain Knowledge Template
                      (plus universal types).
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
            </div>

            {/* outline (not ghost) so Clear reads as a button next to the other
                toolbar controls - matches RefreshButton's variant/size. */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}

            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <RefreshButton onClick={handleRefresh} loading={refreshing} />

              {/* Registry reload is exposed only via the contextual "Sync Now"
                  button in the out-of-sync banner, which appears precisely when
                  a rebuild is needed. A standalone always-visible "Reload Entity"
                  button duplicated that action and was easily confused with the
                  plain display Refresh above. */}
              <Button onClick={handleOpenCreateModal}>
                <Plus className="h-4 w-4 mr-2" />
                Create Entity
              </Button>
            </div>
          </div>

          {/* Registry Sync Alert */}
          {statistics && !statistics.in_sync && (
            <div className="flex items-center justify-between p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400" />
                <div>
                  <span className="font-medium text-amber-300">Registry Out of Sync</span>
                  <span className="text-amber-200/80 ml-2">
                    The entity type registry is not synchronized with the database. Click 'Sync Now' to sync.
                  </span>
                </div>
              </div>
              <Button size="sm" onClick={handleReloadRegistry} disabled={reloadingRegistry}>
                {reloadingRegistry ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Sync Now
              </Button>
            </div>
          )}

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card className="bg-card/40">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Total Entities</p>
                  <span className="p-1.5 rounded-md border border-border bg-muted/40">
                    <Boxes className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                </div>
                <p className="text-3xl font-bold">{totalTypes}</p>
                <p className="text-xs text-muted-foreground">{typesWithVocab} with vocabulary</p>
              </CardContent>
            </Card>
            <Card className="bg-card/40">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">System Core</p>
                  <span className="p-1.5 rounded-md border border-border bg-muted/40">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                </div>
                <p className="text-3xl font-bold">{systemTypes}</p>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary/60"
                    style={{ width: `${totalTypes > 0 ? (systemTypes / totalTypes) * 100 : 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card/40">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Custom Domain</p>
                  <span className="p-1.5 rounded-md border border-border bg-muted/40">
                    <Edit className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                </div>
                <p className="text-3xl font-bold">{customTypes}</p>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-400/60"
                    style={{ width: `${totalTypes > 0 ? (customTypes / totalTypes) * 100 : 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card/40">
              <CardContent className="p-4 space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Avg Confidence</p>
                <div className="flex items-center justify-between">
                  <p className="text-3xl font-bold text-primary">{(avgConfidence * 100).toFixed(0)}%</p>
                  <WeightRing value={avgConfidence} className="h-11 w-11 text-primary" />
                </div>
                <p className="text-xs text-muted-foreground">across this page</p>
              </CardContent>
            </Card>
          </div>

          {/* Entity Graph Nodes */}
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Entity Graph Nodes</h2>
            <div className="flex items-center gap-1 rounded-lg border border-border bg-card/40 p-1">
              <button
                type="button"
                aria-label="Grid view"
                onClick={() => setViewMode('grid')}
                className={`rounded-md p-1.5 transition-colors ${viewMode === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="List view"
                onClick={() => setViewMode('list')}
                className={`rounded-md p-1.5 transition-colors ${viewMode === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <Card>
              <CardContent className="p-0">
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner message="Loading entity types..." fullScreen={false} />
                </div>
              </CardContent>
            </Card>
          ) : totalItems === 0 ? (
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-col items-center justify-center py-12">
                  <Tag className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No entity types found</p>
                  <p className="text-muted-foreground">
                    {hasActiveFilters
                      ? 'Try adjusting your search or filters.'
                      : 'Create a new entity type to get started.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {sortedEntityTypes.map((entityType) => (
                <button
                  key={entityType.id}
                  type="button"
                  onClick={() => handleOpenEditModal(entityType)}
                  className="text-left rounded-xl border border-border bg-card/40 hover:border-primary/50 transition-colors p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${entityType.is_active ? 'bg-emerald-400' : 'bg-red-400'}`}
                        aria-hidden="true"
                      />
                      {entityType.is_system ? 'SYS' : 'CUS'}-{String(entityType.id).padStart(2, '0')}
                    </span>
                    {entityType.is_system && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                  </div>
                  <div>
                    <h3 className="font-mono text-lg font-bold tracking-wide truncate">{entityType.name}</h3>
                    <p className="font-mono text-xs text-muted-foreground truncate">
                      @{entityType.label.toLowerCase().replace(/\s+/g, '_')}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="outline" className={getCategoryColor(entityType.category)}>
                      {getCategoryInfo(entityType.category).label}
                    </Badge>
                    {entityType.parent_type && (
                      <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                        Parent: {entityType.parent_type}
                      </Badge>
                    )}
                    {!entityType.is_active && (
                      <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  {entityType.vocabulary && entityType.vocabulary.length > 0 && (
                    <p className="text-xs text-muted-foreground truncate">
                      {entityType.vocabulary.slice(0, 4).join(', ')}
                      {entityType.vocabulary.length > 4 ? '…' : ''}
                    </p>
                  )}
                  <div className="flex items-end justify-between pt-2 border-t border-border/60">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Weight</p>
                      <p className="font-mono text-base font-semibold">{entityType.importance_weight.toFixed(2)}</p>
                    </div>
                    <WeightRing value={entityType.importance_weight} className="h-9 w-9 text-primary" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <>
            {/* Mobile / tablet card list (< 1280px - iPad Pro 12.9" portrait at 1024px) */}
            <div className="xl:hidden divide-y">
              {sortedEntityTypes.map((entityType) => (
                <div key={entityType.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {entityType.is_system && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent className="dark">System type (protected)</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <span className="font-mono text-sm font-semibold break-all">{entityType.name}</span>
                    </div>
                    <Badge
                      variant="outline"
                      className={entityType.is_active
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                        : 'bg-red-500/10 text-red-400 border-red-500/30'}
                    >
                      {entityType.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <div className="text-sm">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Label</span>
                    <p className="font-medium">{entityType.label}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className={getCategoryColor(entityType.category)}>
                      {getCategoryInfo(entityType.category).label}
                    </Badge>
                    {entityType.parent_type && (
                      <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                        Parent: {entityType.parent_type}
                      </Badge>
                    )}
                    <Badge variant="outline" className={getImportanceColor(entityType.importance_weight)}>
                      Weight: {entityType.importance_weight.toFixed(1)}
                    </Badge>
                  </div>

                  {entityType.vocabulary && entityType.vocabulary.length > 0 && (
                    <div className="text-xs">
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Vocabulary</span>
                      <p className="text-muted-foreground break-words">
                        {entityType.vocabulary.slice(0, 5).join(', ')}
                        {entityType.vocabulary.length > 5 ? '…' : ''}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEditModal(entityType)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {!entityType.is_system && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteConfirm(entityType.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1 text-destructive" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden xl:block overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/30 sticky top-0 z-10">
                  <TableRow className="border-b">
                    <SortableTableHeader
                      column="name"
                      label="Name"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <TableHead className="font-medium text-foreground py-4">Label</TableHead>
                    <TableHead className="font-medium text-foreground py-4">Category</TableHead>
                    <TableHead className="font-medium text-foreground py-4">Parent</TableHead>
                    <SortableTableHeader
                      column="importance_weight"
                      label="Weight"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <TableHead className="font-medium text-foreground py-4">Vocabulary</TableHead>
                    <TableHead className="font-medium text-foreground py-4">Active</TableHead>
                    <TableHead className="font-medium text-foreground py-4 w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedEntityTypes.map((entityType) => (
                    <TableRow key={entityType.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {entityType.is_system && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Lock className="h-3 w-3 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="dark">System type (protected)</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <span className="font-mono text-sm font-semibold">{entityType.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{entityType.label}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getCategoryColor(entityType.category)}>
                          {getCategoryInfo(entityType.category).label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {entityType.parent_type ? (
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="bg-muted text-muted-foreground border-border">{entityType.parent_type}</Badge>
                            {entityType.collapse_to_parent && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">alias</Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="dark">Synonym collapsed into {entityType.parent_type}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getImportanceColor(entityType.importance_weight)}>
                          {entityType.importance_weight.toFixed(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {entityType.vocabulary && entityType.vocabulary.length > 0 ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-default">
                                  {entityType.vocabulary.slice(0, 3).join(', ')}
                                  {entityType.vocabulary.length > 3 ? '...' : ''}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="dark">
                                <p className="max-w-xs">{entityType.vocabulary.join(', ')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={entityType.is_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}>
                          {entityType.is_active ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenEditModal(entityType)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="dark">
                                {entityType.is_system ? 'Edit (Name/Code protected)' : 'Edit'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {!entityType.is_system && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteConfirm(entityType.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="dark">Delete</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
                </>
              </CardContent>
            </Card>
          )}

          {/* Pagination (server-driven). Rendered outside the grid/list branches
              so both view modes get it - it used to live inside the list-view
              Card, which left the default grid view with no way off page 1.
              Hidden when everything fits on one page. */}
          {!loading && totalPages > 1 && (
            <>
              <div className="hidden xl:block">
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                />
              </div>
              <div className="xl:hidden">
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  isMobile
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Entity Configuration Modal */}
      <Dialog open={editModalVisible} onOpenChange={setEditModalVisible}>
        <DialogContent className="dark bg-background text-foreground max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-border space-y-1 text-left">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <Network className="h-3.5 w-3.5" />
              Entity Configuration
            </p>
            <DialogTitle className="font-mono text-xl tracking-wide text-primary">
              {editingEntityType ? editingEntityType.name : 'NEW ENTITY TYPE'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingEntityType
                ? 'Edit the configuration of this entity type'
                : 'Create a new entity type for knowledge extraction'}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
            {/* Core Identity */}
            <ConfigSectionDivider icon={<Fingerprint className="h-3.5 w-3.5" />} label="Core Identity" />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="label">Display Label</Label>
                  <span className="text-destructive">*</span>
                </div>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="Entity Label"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark">
                    <SelectItem value="core">Core</SelectItem>
                    <SelectItem value="operational">Operational</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="temporal">Temporal</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="natural">Natural</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="name">System Identifier</Label>
                {!editingEntityType && <span className="text-destructive">*</span>}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="dark">
                      Uppercase identifier (e.g., STANDARDS_BODY). Locked after creation.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                  placeholder="ENTITY_NAME"
                  disabled={!!editingEntityType}
                  className="pl-10 font-mono tracking-wide"
                />
              </div>
            </div>

            {/* Extraction Intelligence */}
            <ConfigSectionDivider icon={<Crosshair className="h-3.5 w-3.5" />} label="Extraction Intelligence" />

            <div className="space-y-2">
              <Label htmlFor="description">Semantic Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this entity type"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 items-start">
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="parent_type">Inheritance (Parent Type)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="dark">Inherit from a base type (e.g., ORGANIZATION)</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {/* Inline searchable picker (NOT a Radix Popover portal, so
                    the mouse wheel scrolls the list inside the Dialog). Curated
                    types only (auto-discovered noise hidden); any curated type
                    can be a parent/master, excluding the edited type. */}
                <div className="relative" ref={parentPickerRef}>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={parentPickerOpen}
                    onClick={() => setParentPickerOpen(o => !o)}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">
                      {selectedParent
                        ? `${selectedParent.label} (${selectedParent.name})`
                        : formData.parent_type || 'None'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                  {parentPickerOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
                      <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                          autoFocus
                          value={parentSearch}
                          onChange={(e) => setParentSearch(e.target.value)}
                          placeholder="Search types…"
                          className="flex h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto overscroll-contain p-1">
                        <button
                          type="button"
                          onClick={() => {
                            // No parent => collapse-into-parent is meaningless,
                            // so switch it off when the user clears the parent.
                            setFormData({ ...formData, parent_type: '', collapse_to_parent: false });
                            setParentPickerOpen(false);
                            setParentSearch('');
                          }}
                          className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                        >
                          <Check className={`mr-2 h-4 w-4 ${!formData.parent_type ? 'opacity-100' : 'opacity-0'}`} />
                          None
                        </button>
                        {filteredParentCandidates.map(e => (
                          <button
                            key={e.name}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, parent_type: e.name });
                              setParentPickerOpen(false);
                              setParentSearch('');
                            }}
                            className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                          >
                            <Check className={`mr-2 h-4 w-4 shrink-0 ${formData.parent_type === e.name ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="truncate">{e.label} ({e.name})</span>
                          </button>
                        ))}
                        {filteredParentCandidates.length === 0 && (
                          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                            No types found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card/40 p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="importance_weight">Confidence Weight</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="dark">Weight for relationship confidence (0.0-1.0)</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <span className="font-mono text-sm font-semibold">{formData.importance_weight.toFixed(2)}</span>
                </div>
                <Slider
                  id="importance_weight"
                  min={0}
                  max={1}
                  step={0.05}
                  value={[formData.importance_weight]}
                  onValueChange={([v]) => setFormData({ ...formData, importance_weight: v })}
                />
                <div className="flex items-center justify-between font-mono text-[11px] text-muted-foreground">
                  <span>0.0 (Lenient)</span>
                  <span>1.0 (Strict)</span>
                </div>
              </div>
            </div>

            {/* Pattern Validation */}
            <ConfigSectionDivider icon={<ListChecks className="h-3.5 w-3.5" />} label="Pattern Validation" />

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="patterns">Regular Expressions</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="dark">One pattern per line for advanced matching</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] tracking-wider bg-muted text-muted-foreground border-border">
                  REGEX ENGINE V2
                </Badge>
              </div>
              <CodeEditorShell filename="patterns.re" value={formData.patterns} minLines={3}>
                <Textarea
                  id="patterns"
                  value={formData.patterns}
                  onChange={(e) => setFormData({ ...formData, patterns: e.target.value })}
                  placeholder="Enter regex patterns (one per line)"
                  rows={3}
                  className="flex-1 font-mono text-sm leading-6 bg-transparent border-0 rounded-none min-h-0 py-2 resize-y focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </CodeEditorShell>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="vocabulary">Seed Vocabulary (Aliases)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="dark">Comma-separated keywords that trigger this entity type</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <CodeEditorShell filename="vocabulary.csv" value={formData.vocabulary} minLines={2}>
                <Textarea
                  id="vocabulary"
                  value={formData.vocabulary}
                  onChange={(e) => setFormData({ ...formData, vocabulary: e.target.value })}
                  placeholder="ANSI, OSHA, EPA, ISO (comma-separated)"
                  rows={2}
                  className="flex-1 font-mono text-sm leading-6 bg-transparent border-0 rounded-none min-h-0 py-2 resize-y focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </CodeEditorShell>
            </div>

            {/* Pipeline Settings */}
            <ConfigSectionDivider icon={<Settings2 className="h-3.5 w-3.5" />} label="Pipeline Settings" />

            <div className="rounded-xl border border-border bg-card/40 p-4 flex items-center justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="is_active" className="text-sm font-semibold">Entity Pipeline Active</Label>
                <p className="text-sm text-muted-foreground">
                  Enable extraction of this entity across all currently active intelligence pipelines.
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="rounded-xl border border-border bg-card/40 p-4 flex items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Label htmlFor="is_universal" className="text-sm font-semibold">Universal Type Definition</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="dark max-w-xs">
                        When on, this type applies to every industry - appears
                        in every Domain Knowledge Template without needing a
                        per-industry whitelist entry. Use for cross-industry
                        types like PERSON, ORGANIZATION, DATE.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-sm text-muted-foreground">
                  Make available globally outside specific, isolated knowledge graphs.
                </p>
              </div>
              <Switch
                id="is_universal"
                checked={formData.is_universal}
                onCheckedChange={(checked) => setFormData({ ...formData, is_universal: checked })}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="collapse_to_parent">Collapse into Parent</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="dark max-w-xs">
                      Treat this type as a SYNONYM of its Parent Type (the
                      master). Future extractions are redirected to the
                      master, existing entities are migrated/merged into it,
                      and this type is deactivated. Requires a Parent Type.
                      Use only for true synonyms (e.g. SERVICE_TYPE → SERVICE),
                      not genuine hierarchy.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="pt-2">
                <Switch
                  id="collapse_to_parent"
                  checked={formData.collapse_to_parent}
                  disabled={!formData.parent_type}
                  onCheckedChange={(checked) => setFormData({ ...formData, collapse_to_parent: checked })}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border sm:justify-between gap-2">
            <div>
              {editingEntityType && !editingEntityType.is_system && (
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleDeleteConfirm(editingEntityType.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Entity
                </Button>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={handleCloseModal}>
                Discard Changes
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Deploy Configuration
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteConfirmVisible}
        onOpenChange={setDeleteConfirmVisible}
        title="Delete Entity Type"
        description="Are you sure you want to delete this entity type? This action cannot be undone. Existing entities of this type will not be affected but new entities cannot be classified as this type."
        onConfirm={handleDelete}
        loading={deleting}
        contentClassName="dark bg-background text-foreground"
      />
    </div>
  );
};

export default KBEntityTypesPage;
